/**
 * Compute clients: the worker pool for the browser, an in-process
 * client for tests and fallback.
 *
 * Pool shape (brief Section 16 table): one worker per logical core
 * minus one, minimum one, capped at four — runs are long and
 * cell-shaped, and more workers than that mostly contend for memory.
 * Jobs dispatch to the least busy worker; progress callbacks cross
 * the boundary through Comlink proxies. The main thread never parses
 * or fits (brief 4.4, CMP-05).
 */

import * as Comlink from 'comlink';

import { createComputeHandler } from '../pipeline/handler.js';
import type { ComputeApi } from './protocol.js';

/** In-process client: the same handler, no worker. Used by tests. */
export class InProcessClient implements ComputeApi {
  readonly #handler: ComputeApi = createComputeHandler();

  parseFile(
    bytes: Uint8Array,
    filename: string,
  ): ReturnType<ComputeApi['parseFile']> {
    return this.#handler.parseFile(bytes, filename);
  }
  assess(table: Parameters<ComputeApi['assess']>[0]) {
    return this.#handler.assess(table);
  }
  runQc(
    table: Parameters<ComputeApi['runQc']>[0],
    profile: Parameters<ComputeApi['runQc']>[1],
    context: Parameters<ComputeApi['runQc']>[2],
  ) {
    return this.#handler.runQc(table, profile, context);
  }
  proposeIndicatorRule(
    table: Parameters<ComputeApi['proposeIndicatorRule']>[0],
    column: string,
  ) {
    return this.#handler.proposeIndicatorRule(table, column);
  }
  previewFeasibility(job: Parameters<ComputeApi['previewFeasibility']>[0]) {
    return this.#handler.previewFeasibility(job);
  }
  run(
    job: Parameters<ComputeApi['run']>[0],
    onProgress: Parameters<ComputeApi['run']>[1],
  ) {
    return this.#handler.run(job, onProgress);
  }
}

interface PoolWorker {
  readonly api: Comlink.Remote<ComputeApi>;
  busy: number;
  readonly worker: Worker;
}

/** Browser client: a small Comlink pool over compute.worker.ts. */
export class WorkerPoolClient implements ComputeApi {
  readonly #pool: PoolWorker[] = [];

  constructor(size?: number) {
    const cores =
      typeof navigator !== 'undefined'
        ? (navigator.hardwareConcurrency ?? 2)
        : 2;
    const count = Math.max(1, Math.min(size ?? cores - 1, 4));
    for (let i = 0; i < count; i++) {
      const worker = new Worker(
        new URL('./compute.worker.ts', import.meta.url),
        { type: 'module' },
      );
      this.#pool.push({ api: Comlink.wrap<ComputeApi>(worker), busy: 0, worker });
    }
  }

  #pick(): PoolWorker {
    let best = this.#pool[0];
    for (const entry of this.#pool) {
      if (entry.busy < best.busy) best = entry;
    }
    return best;
  }

  async #dispatch<T>(call: (api: Comlink.Remote<ComputeApi>) => Promise<T>): Promise<T> {
    const entry = this.#pick();
    entry.busy++;
    try {
      return await call(entry.api);
    } finally {
      entry.busy--;
    }
  }

  parseFile(bytes: Uint8Array, filename: string) {
    return this.#dispatch((api) => api.parseFile(bytes, filename));
  }
  assess(table: Parameters<ComputeApi['assess']>[0]) {
    return this.#dispatch((api) => api.assess(table));
  }
  runQc(
    table: Parameters<ComputeApi['runQc']>[0],
    profile: Parameters<ComputeApi['runQc']>[1],
    context: Parameters<ComputeApi['runQc']>[2],
  ) {
    return this.#dispatch((api) => api.runQc(table, profile, context));
  }
  proposeIndicatorRule(
    table: Parameters<ComputeApi['proposeIndicatorRule']>[0],
    column: string,
  ) {
    return this.#dispatch((api) => api.proposeIndicatorRule(table, column));
  }
  previewFeasibility(job: Parameters<ComputeApi['previewFeasibility']>[0]) {
    return this.#dispatch((api) => api.previewFeasibility(job));
  }
  run(
    job: Parameters<ComputeApi['run']>[0],
    onProgress: Parameters<ComputeApi['run']>[1],
  ) {
    return this.#dispatch((api) =>
      api.run(job, Comlink.proxy(onProgress)),
    );
  }

  terminate(): void {
    for (const entry of this.#pool) entry.worker.terminate();
    this.#pool.length = 0;
  }
}
